/**
 * The tiny rich-text subset a form can say things in (U5).
 *
 * Four constructs and nothing else: `**bold**`, `*italic*`, `[text](https://…)`
 * and line breaks (one newline is a break, a blank line starts a new
 * paragraph). No HTML goes in — a `<script>` typed here is four characters of
 * text — and none comes out except what `RichText.tsx` emits from this tree,
 * so a builder's paragraph, a section's description and a field's help text
 * can carry a link without ever carrying markup.
 *
 * Links are http(s) only. Anything else in the parentheses (`javascript:`,
 * `data:`, a bare path) is dropped and its text kept, so a bad link becomes
 * plain words rather than a live one.
 */

export type RichNode =
  | string
  | { t: 'b'; c: RichNode[] }
  | { t: 'i'; c: RichNode[] }
  | { t: 'a'; href: string; c: RichNode[] }
  | { t: 'br' };

/** One paragraph per blank-line-separated block; a bare newline is a `br`. */
export type RichBlock = RichNode[];

/** `[text](url)` — the url may hold one level of balanced parentheses, as CommonMark allows. */
const LINK = /^\[([^\]\n]+)\]\(((?:[^\s()]|\([^\s()]*\))*)\)/;
const SAFE_HREF = /^https?:\/\/[^\s)]+$/i;

/** Inline markup of one line, no paragraph or break handling. */
function parseInline(s: string): RichNode[] {
  const out: RichNode[] = [];
  let buf = '';
  let i = 0;
  const flush = () => { if (buf) { out.push(buf); buf = ''; } };
  while (i < s.length) {
    if (s[i] === '[') {
      const m = LINK.exec(s.slice(i));
      if (m) {
        flush();
        const inner = parseInline(m[1]);
        if (SAFE_HREF.test(m[2])) out.push({ t: 'a', href: m[2], c: inner });
        else out.push(...inner);
        i += m[0].length;
        continue;
      }
    }
    if (s.startsWith('**', i)) {
      const end = s.indexOf('**', i + 2);
      if (end > i + 2) {
        flush();
        out.push({ t: 'b', c: parseInline(s.slice(i + 2, end)) });
        i = end + 2;
        continue;
      }
    }
    if (s[i] === '*') {
      const end = s.indexOf('*', i + 1);
      if (end > i + 1) {
        flush();
        out.push({ t: 'i', c: parseInline(s.slice(i + 1, end)) });
        i = end + 1;
        continue;
      }
    }
    buf += s[i];
    i += 1;
  }
  flush();
  return out;
}

/** Inline nodes for one block: lines joined by `br`. */
function parseLines(block: string): RichNode[] {
  const out: RichNode[] = [];
  block.split('\n').forEach((line, i) => {
    if (i > 0) out.push({ t: 'br' });
    out.push(...parseInline(line));
  });
  return out;
}

/** The text as paragraphs (split on blank lines), each a list of inline nodes. */
export function parseRichText(src: string): RichBlock[] {
  const text = String(src ?? '').replace(/\r\n?/g, '\n').trim();
  if (!text) return [];
  return text.split(/\n[ \t]*\n+/).map((b) => parseLines(b.trim()));
}

/** Everything inline: one block, paragraphs joined by a `br` (help text, a description in a table). */
export function parseInlineText(src: string): RichNode[] {
  const blocks = parseRichText(src);
  const out: RichNode[] = [];
  blocks.forEach((b, i) => {
    if (i > 0) out.push({ t: 'br' });
    out.push(...b);
  });
  return out;
}

function nodeText(n: RichNode): string {
  if (typeof n === 'string') return n;
  if (n.t === 'br') return '\n';
  return n.c.map(nodeText).join('');
}

/** The words with the markup taken off — for a PDF, an email table, a title attribute. */
export function plainText(src: string): string {
  return parseRichText(src).map((b) => b.map(nodeText).join('')).join('\n\n');
}

/** Whether rendering this text as rich would differ from rendering it plain. */
export function hasMarkup(src: string): boolean {
  const s = String(src ?? '');
  return /\*\*|\*|\[[^\]]+\]\(|\n/.test(s);
}
